import { useChangeFilterTrack } from "@/_old/features/analytics";
import { Button } from "@/core/components/ui/button";
import { Checkbox } from "@/core/components/ui/checkbox";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/core/components/ui/command";
import { Label } from "@/core/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/core/components/ui/popover";
import { useDashboardFilters } from "@/dashboard/store/useDashboardFilters";
import { ChevronDown } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { City } from "./types";

interface CityInputProps extends React.ComponentPropsWithoutRef<
  typeof Popover
> {
  location: string;
  data: City[];
  isLoading: boolean;
  selectedSpaces: { id: string; name: string; city: string }[] | null;
  onSubmit?: boolean;
  onChange?: (value: City[] | null) => void;
}

export function CityInput({
  location,
  data,
  isLoading,
  selectedSpaces,
  onSubmit,
  onChange,
  ...props
}: CityInputProps) {
  const { t } = useTranslation(["dashboard"]);
  const [filteredData, setFilteredData] = useState<City[]>([]);
  const { onChangeFilter } = useChangeFilterTrack();
  const { filters, setFilters } = useDashboardFilters();
  const [selectedItem, setSelectedItem] = useState<City[] | null>(null);

  const handleChange = useCallback(
    (value: City[] | null) => {
      //   onChangeFilter("FILTER_MEMBERS", eventCategory);

      if (onSubmit) {
        onChange?.(value as City[]);
      } else {
        setFilters({ cities: value as City[] });
      }
    },
    [onChange, onSubmit, setFilters],
  );

  const handleSelect = useCallback(
    (city: City | null) => {
      if (city === null) {
        setSelectedItem(null);
        handleChange(null);
      } else {
        setSelectedItem((prev) => {
          if (!prev) {
            const newSelection = [city];
            handleChange(newSelection);
            return newSelection;
          }

          const isAlreadySelected = prev.some(
            (selectedCity) => selectedCity.id === city.id,
          );

          if (isAlreadySelected) {
            const newSelection = prev.filter(
              (selectedCity) => selectedCity.id !== city.id,
            );
            handleChange(newSelection.length > 0 ? newSelection : null);
            return newSelection.length > 0 ? newSelection : null;
          } else {
            const newSelection = [...prev, city];
            handleChange(newSelection);
            return newSelection;
          }
        });
      }
    },
    [handleChange],
  );

  useEffect(() => {
    setSelectedItem(filters.cities || null);
  }, [filters.cities]);

  useEffect(() => {
    if (selectedSpaces && selectedSpaces.length > 0) {
      setFilteredData(
        data.filter((city) =>
          selectedSpaces?.some((space) => space.city === city.id),
        ),
      );
    } else {
      setFilteredData(data);
    }
  }, [selectedSpaces, data]);

  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor={`${location}-city-filter`}>
        {t("city-filter-label") as string}
      </Label>

      <Popover {...props}>
        <PopoverTrigger asChild>
          <Button
            id={`${location}-city-filter`}
            variant="outline"
            disabled={isLoading}
            className="justify-between w-full bg-white"
          >
            <span className="overflow-hidden font-normal text-ellipsis">
              {selectedItem && selectedItem.length > 0
                ? selectedItem.length === 1
                  ? `${selectedItem[0].name}`
                  : (t("city-filter-selected-multiple", {
                      count: selectedItem.length,
                    }) as string)
                : (t("city-filter-placeholder") as string)}
            </span>
            <ChevronDown className="opacity-50 size-4" />
          </Button>
        </PopoverTrigger>

        <PopoverContent className="p-0 w-[500px] max-w-[500px]">
          <Command>
            <CommandInput
              placeholder={t("city-filter-search-placeholder") as string}
              className="h-9"
            />
            <CommandList>
              <CommandEmpty>{t("city-filter-empty-state")}</CommandEmpty>
              <CommandGroup>
                <CommandItem
                  value=""
                  onSelect={() => {
                    handleSelect(null);
                  }}
                >
                  <Checkbox
                    className="mr-2"
                    checked={!selectedItem || selectedItem.length === 0}
                  />

                  {t("city-filter-placeholder")}
                </CommandItem>
                {filteredData.map((city, index) => (
                  <CommandItem
                    key={`${city.id}-${index}`}
                    value={city.name}
                    onSelect={() => {
                      handleSelect(city);
                    }}
                  >
                    <Checkbox
                      className="mr-2"
                      checked={
                        selectedItem?.some(
                          (selectedMember) => selectedMember.id === city.id,
                        ) ||
                        selectedItem?.length === 0 ||
                        !selectedItem
                      }
                    />

                    {city.name}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
